import {
  Trophy,
  RefreshCw,
  ArrowRight,
  Sparkles,
  Target,
  Award,
  ThumbsUp,
  ThumbsDown,
  Meh
} from 'lucide-react';

import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';


// Outcome configurations
const OUTCOME_CONFIG = {
  optimal: {
    title: 'Perfect!',
    icon: Trophy,
    color: 'text-green-600 dark:text-green-400',
    bgColor: 'bg-green-50 dark:bg-green-900/30',
    borderColor: 'border-green-200 dark:border-green-800',
    description: 'You made all the best choices!'
  },
  good: {
    title: 'Well Done!',
    icon: ThumbsUp,
    color: 'text-blue-600 dark:text-blue-400',
    bgColor: 'bg-blue-50 dark:bg-blue-900/30',
    borderColor: 'border-blue-200 dark:border-blue-800',
    description: 'A good outcome!'
  },
  neutral: {
    title: 'Completed',
    icon: Meh,
    color: 'text-gray-600 dark:text-gray-400',
    bgColor: 'bg-gray-50 dark:bg-gray-800',
    borderColor: 'border-gray-200 dark:border-gray-700',
    description: 'An acceptable outcome.'
  },
  poor: {
    title: 'Not Great',
    icon: ThumbsDown,
    color: 'text-orange-600 dark:text-orange-400',
    bgColor: 'bg-orange-50 dark:bg-orange-900/30',
    borderColor: 'border-orange-200 dark:border-orange-800',
    description: 'There were better choices.'
  },
  failure: {
    title: 'Try Again',
    icon: Target,
    color: 'text-red-600 dark:text-red-400',
    bgColor: 'bg-red-50 dark:bg-red-900/30',
    borderColor: 'border-red-200 dark:border-red-800',
    description: 'This didn\'t go well. Learn and retry!'
  }
};

// ============================================
// Points Breakdown
// ============================================
const PointsBreakdown = ({ points }) => {
  if (!points) return null;

  return (
    <div className="space-y-2 text-sm">
      <div className="flex justify-between">
        <span className="text-gray-600 dark:text-gray-400">Base Points:</span>
        <span className="font-medium text-green-600 dark:text-green-400">+{points.base_points}</span>
      </div>
      {points.bonus_points > 0 && (
        <div className="flex justify-between">
          <span className="text-gray-600 dark:text-gray-400">Ending Bonus:</span>
          <span className="font-medium text-purple-600 dark:text-purple-400">+{points.bonus_points}</span>
        </div>
      )}
      <div className="flex justify-between pt-2 border-t dark:border-gray-700 font-semibold">
        <span className="text-gray-900 dark:text-gray-100">Total:</span>
        <span className="text-lg text-blue-600 dark:text-blue-400">+{points.total_points}</span>
      </div>
    </div>
  );
};

// ============================================
// Main ScenarioResult Component
// ============================================
const ScenarioResult = ({ scenario, result, onPlayAgain, onExit }) => {
  const outcomeType = result.ending_type || 'neutral';
  const config = OUTCOME_CONFIG[outcomeType] || OUTCOME_CONFIG.neutral;
  const Icon = config.icon;

  return (
    <div className="max-w-md mx-auto">
      <Card className={`${config.bgColor} border-2 ${config.borderColor}`}>
        <CardContent className="py-8 text-center">
          {/* Result Icon */}
          <div className={`inline-flex p-4 rounded-full ${config.bgColor} mb-4`}>
            <Icon className={`h-12 w-12 ${config.color}`} />
          </div>

          {/* Title */}
          <h2 className={`text-2xl font-bold ${config.color} mb-2`}>
            {config.title}
          </h2>

          {/* Optimal Path Badge */}
          {result.optimal_path && (
            <div className="inline-flex items-center gap-1 bg-yellow-100 dark:bg-yellow-900/50 text-yellow-700 dark:text-yellow-300 px-3 py-1 rounded-full text-sm mb-4">
              <Sparkles className="h-4 w-4" />
              Optimal Path!
            </div>
          )}

          {/* Description */}
          <p className="text-gray-600 dark:text-gray-400 mb-6">{config.description}</p>

          {/* Ending Message */}
          {result.ending_message && (
            <div className="bg-white dark:bg-gray-800 rounded-lg p-4 mb-6 text-left">
              <p className="text-gray-800 dark:text-gray-200 whitespace-pre-wrap">{result.ending_message}</p>
            </div>
          )}

          {/* Points Earned */}
          {result.points_earned && (
            <div className="bg-white dark:bg-gray-800 rounded-lg p-4 mb-6">
              <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-3 flex items-center justify-center gap-2">
                <Award className="h-5 w-5 text-yellow-500" />
                Points Earned
              </h3>
              <PointsBreakdown points={result.points_earned} />
            </div>
          )}

          {/* Actions */}
          <div className="space-y-3">
            {/* Play Again - especially encouraged for non-optimal */}
            {!result.optimal_path && (
              <Button
                onClick={onPlayAgain}
                variant="memberPrimary"
                className="w-full"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Try for Optimal
              </Button>
            )}

            {result.optimal_path && (
              <Button
                onClick={onPlayAgain}
                variant="outline"
                className="w-full"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Play Again
              </Button>
            )}

            <Button
              onClick={onExit}
              variant={result.optimal_path ? 'default' : 'outline'}
              className="w-full"
            >
              <ArrowRight className="h-4 w-4 mr-2" />
              Back to Scenarios
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Scenario Info */}
      <div className="mt-4 text-center text-sm text-gray-500 dark:text-gray-400">
        <p className="flex items-center justify-center gap-2">
          <span className="text-xl">{scenario.icon_emoji}</span>
          {scenario.title}
        </p>
      </div>
    </div>
  );
};

export default ScenarioResult;
